import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { decrypt, encrypt } from '../../../common/enc_dec';
import { course, enrollUser } from '../../../slices/courseSlice';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Loader from '../../../components/loader';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import { useModuleSettings } from '../../../components/moduleSettingsContext/ModuleSettingsContext';
import * as XLSX from 'xlsx';

const EnrollmentReport = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = location?.state || {};
    const queryParams = new URLSearchParams(location.search);
    const _id = queryParams.get("id") ? decrypt(queryParams.get("id")) : null;
    const courseId = id || _id;

    const { enrollUserList, loading } = useSelector((state) => state.course);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [completionFilter, setCompletionFilter] = useState("");
    const { filter, setFilter, page, setPage, limit, setLimit } = useModuleSettings();
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (courseId) {
            dispatch(course({ id: courseId }));
            dispatch(enrollUser({ 
                course_id: courseId, 
                period: filter, 
                startDate, 
                endDate, 
                page, 
                limit,
                is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined // Add completion filter
            }));
        }
    }, [dispatch, courseId, filter, page, limit, startDate, endDate, completionFilter]); // Add completionFilter to dependency

    const handleViewUser = (row) => {
        navigate(`/users/view?id=${encrypt(`${row.id}`)}`, { state: { userData: row } });
    };

    // Calculate completed and not completed counts
    const completedCount = enrollUserList?.completed_count || 0;
    const notCompletedCount = enrollUserList?.not_completed_count || 0;
    const totalEnrollments = (enrollUserList?.paging?.total || 0);

    const columns = [
        {
            name: "Sr No",
            selector: (row, index) => (page - 1) * limit + index + 1,
            sortable: false,
            width: "80px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "User ID",
            selector: (row) => row.user_id || "NA",
            sortable: true,
            width: "100px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Full Name",
            selector: (row) => <span className="text-capitalize">{row.enroll_user?.full_name || "NA"}</span>,
            sortable: true,
            minWidth: "200px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Email",
            selector: (row) => row.enroll_user?.email || "NA",
            sortable: true,
            minWidth: "250px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Phone Number",
            selector: (row) => (
                <>
                    {row.enroll_user?.country_code && row.enroll_user?.phone_number ?
                        `+${row.enroll_user.country_code} ${row.enroll_user.phone_number}` :
                        "NA"}
                </>
            ),
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Purchase Date",
            selector: (row) => row.purchage_date ? moment(row.purchage_date).format("DD-MMM-YYYY") : "NA",
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Completion Date",
            selector: (row) => row.completion_date ? moment(row.completion_date).format("DD-MMM-YYYY") : "NA",
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Course Status",
            selector: (row) => (
                <span className={`badge ${row.is_test_completed ? 'bg-success' : 'bg-warning'}`}>
                    {row.is_test_completed ? 'Completed' : 'In Progress'}
                </span>
            ),
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Certificate",
            selector: (row) => (
                row.certificate ?
                    <a href={row.certificate} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                        View
                    </a> :
                    <span className="badge bg-secondary">Not Available</span>
            ),
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "View User",
            selector: (row) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <button className="btn" onClick={() => handleViewUser(row)}>
                        <i
                            className="fe-eye font-22 avatar-title text-primary"
                            style={{ color: "blue" }}
                        />
                    </button>
                </div>
            ),
            minWidth: "250px",
            style: {
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
            },
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headCells: {
            style: {
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                justifyContent: "center",
            },
        },
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    // Handler for completion filter change
    const handleCompletionFilterChange = (event) => {
        setCompletionFilter(event.target.value);
        // Reset pagination to first page when changing filters
        setPage(1);
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate && moment(startDate).isBefore(endDate)) {
            dispatch(
                enrollUser({
                    course_id: courseId,
                    period: "custom",
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined // Add completion filter
                })
            );
        } else {
            Swal.fire({
                title: 'Invalid Dates',
                text: 'Please select valid start and end dates.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleExportData = useCallback(async () => {
        try {
            setIsExporting(true);

            // Fetch all data for export (you can adjust parameters as needed)
            await dispatch(enrollUser({
                course_id: courseId,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                is_report: 1,
                is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined // Add completion filter to export
            }))
                .then((response) => {
                    if (response.payload && response.payload.data) {
                        // Map the data to the format we want for Excel
                        const exportData = response.payload.data.map((row, index) => ({
                            "Sr No": index + 1,
                            "User ID": row.user_id || "NA",
                            "Full Name": row.enroll_user?.full_name || "NA",
                            "Email": row.enroll_user?.email || "NA",
                            "Phone Number": row.enroll_user?.country_code && row.enroll_user?.phone_number
                                ? `+${row.enroll_user.country_code} ${row.enroll_user.phone_number}`
                                : "NA",
                            "Purchase Date": row.purchage_date
                                ? moment(row.purchage_date).format("DD-MMM-YYYY")
                                : "NA",
                            "Completion Date": row.completion_date
                                ? moment(row.completion_date).format("DD-MMM-YYYY")
                                : "NA",
                            "Course Status": row.is_test_completed ? "Completed" : "In Progress",
                            "Certificate": row.certificate ? "Available" : "Not Available"
                        }));

                        // Create summary data
                        const completedCount = response.payload.completed_count || 0;
                        const notCompletedCount = response.payload.not_completed_count || 0;
                        const totalEnrollments = completedCount + notCompletedCount;

                        const summaryData = [
                            {
                                "Report Details": "Course Enrollment Summary",
                                "Value": ""
                            },
                            {
                                "Report Details": "Total Enrollments",
                                "Value": totalEnrollments
                            },
                            {
                                "Report Details": "Completed",
                                "Value": completedCount
                            },
                            {
                                "Report Details": "In Progress",
                                "Value": notCompletedCount
                            },
                            {
                                "Report Details": "Export Date",
                                "Value": moment().format("DD-MMM-YYYY HH:mm:ss")
                            }
                        ];

                        // Create workbook and worksheets
                        const workbook = XLSX.utils.book_new();

                        // Add summary sheet
                        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
                        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

                        // Add enrollment data sheet
                        const enrollmentSheet = XLSX.utils.json_to_sheet(exportData);
                        XLSX.utils.book_append_sheet(workbook, enrollmentSheet, "Enrollment Data");

                        // Save the file
                        const fileName = `Training_Progress_and_Completion_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

                        Swal.fire({
                            icon: "success",
                            title: "Export Successful",
                            text: `Data exported to ${fileName}`,
                        });
                        dispatch(enrollUser({ 
                            course_id: courseId, 
                            period: filter, 
                            startDate, 
                            endDate, 
                            page, 
                            limit,
                            is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined // Add completion filter to refresh
                        }));
                    } else {
                        throw new Error("No data available for export");
                    }
                })
                .finally(() => {
                    setIsExporting(false);
                });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.message || "Unable to export enrollment data",
            });
            setIsExporting(false);
        }
    }, [dispatch, courseId, filter, startDate, endDate, completionFilter]); // Add completionFilter to dependency

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="">
            <div className="col-md-12 col-xl-12 mt-1">
                <div className="widget-rounded-circle card">
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col d-flex justify-content-end">
                                <button
                                    onClick={handleExportData}
                                    className="btn btn-outline-warning d-flex align-items-center gap-2 text-black"
                                    disabled={isExporting}
                                >
                                    {isExporting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                src="/assets/images/download.svg"
                                                alt="Download"
                                                style={{ width: "16px", height: "16px", marginRight: "6px" }}
                                                className='text-warning'
                                            />
                                            Generate Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        
                        {/* Updated filter section with completion filter */}
                        <div className="d-flex flex-wrap gap-3 mb-3">
                            <div className="d-flex align-items-end gap-3 flex-wrap">
                                {/* Time Period Filter */}
                                <div>
                                    <label className="form-label">Time Period</label>
                                    <select
                                        className="form-select"
                                        onChange={handleFilterChange}
                                        value={filter}
                                        style={{ minWidth: "150px" }}
                                    >
                                        <option value="">Select</option>
                                        <option value="day">Today</option>
                                        <option value="week">Week</option>
                                        <option value="month">Month</option>
                                        <option value="year">Year</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>

                                {/* Completion Status Filter */}
                                <div>
                                    <label className="form-label">Course Status</label>
                                    <select
                                        className="form-select"
                                        onChange={handleCompletionFilterChange}
                                        value={completionFilter}
                                        style={{ minWidth: "150px" }}
                                    >
                                        <option value="">All Enrollments</option>
                                        <option value="completed">Completed</option>
                                        <option value="in_progress">In Progress</option>
                                    </select>
                                </div>
                            </div>

                            {/* Custom Date Range (shown only when filter is "custom") */}
                            {filter === "custom" && (
                                <div className="row mt-3">
                                    <div className="col">
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => setStartDate(date)}
                                            selectsStart
                                            startDate={startDate}
                                            endDate={endDate}
                                            placeholderText="Start Date"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col">
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date) => setEndDate(date)}
                                            selectsEnd
                                            startDate={startDate}
                                            endDate={endDate}
                                            minDate={startDate}
                                            placeholderText="End Date"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-auto d-flex align-items-center">
                                        <button className="btn btn-primary w-100" onClick={handleCustomDateChange}>
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-4">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h5 className="card-title text-white">Total Enrollments</h5>
                                        <h2 className="mt-3 text-white">{totalEnrollments}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card bg-success text-white">
                                    <div className="card-body">
                                        <h5 className="card-title text-white">Completed</h5>
                                        <h2 className="mt-3 text-white">{completedCount}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card bg-warning text-white">
                                    <div className="card-body">
                                        <h5 className="card-title text-white">In Progress</h5>
                                        <h2 className="mt-3 text-white">{notCompletedCount}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <DataTable
                                title={null}
                                columns={columns}
                                data={enrollUserList?.data || []}
                                pagination
                                paginationServer
                                paginationTotalRows={enrollUserList?.paging?.total}
                                paginationDefaultPage={page}
                                paginationPerPage={limit}
                                paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                onChangeRowsPerPage={(newrow) => setLimit(newrow)}
                                onChangePage={(newPage) => setPage(newPage)}
                                responsive
                                selectableRowsHighlight
                                highlightOnHover
                                striped
                                fixedHeader
                                persistTableHead
                                noDataComponent={<h5 className="p-3">No Enrollment Data Found</h5>}
                                customStyles={customStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnrollmentReport;